"use client";

import { useEffect, useState, useCallback, useRef } from "react";
import dynamic from "next/dynamic";
import { AnimatePresence, motion } from "framer-motion";
import { useAppStore } from "@/stores/app-store";
import { LoginPage, RegisterPage, ForgotPasswordPage, ModuleSelectionPage } from "@/components/oryxos/auth/auth-pages";
import { MarketingPages } from "@/components/oryxos/landing/marketing-pages";
import { applyAccentColor, loadAccentFromStorage } from "@/lib/accent-color";

const CookieConsent = dynamic(() => import("@/components/oryxos/landing/cookie-consent"), { ssr: false });

const LandingPage = dynamic(() => import("@/components/oryxos/landing/landing-page"), { ssr: false });
const DashboardShell = dynamic(() => import("@/components/oryxos/dashboard/dashboard-shell").then(m => ({ default: m.DashboardShell })), { ssr: false });

/* ═══════════════════════════════════════════════════════════════
   ORYXOS — Main Application Entry
   ═══════════════════════════════════════════════════════════════ */

const pageVariants = {
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -12 },
};

const pageTransition = {
  type: "spring" as const,
  stiffness: 260,
  damping: 26,
  mass: 0.8,
};

const MARKETING_VIEWS = ['products', 'pricing', 'solutions', 'partners', 'company', 'careers', 'blog', 'contact', 'community', 'support', 'resources', 'terms', 'privacy'];
const AUTH_VIEWS = ['login', 'register', 'forgot-password', 'module-selection'];

/* ── Auth View Router ──────────────────────────────────────── */
function AuthViewRouter({ view }: { view: string }) {
  switch (view) {
    case "login":
      return <LoginPage />;
    case "register":
      return <RegisterPage />;
    case "forgot-password":
      return <ForgotPasswordPage />;
    case "module-selection":
      return <ModuleSelectionPage />;
    default:
      return <LoginPage />;
  }
}

/* ── Main Page ─────────────────────────────────────────────── */
export default function HomePage() {
  const {
    currentView,
    setCurrentView,
    isAuthenticated,
    setIsAuthenticated,
    setUserId,
    setUserName,
    setUserEmail,
    setUserAvatarUrl,
    setCompanyName,
    setTenantSlug,
    setUserRole,
    setSelectedModules,
    isDark,
    setShowWelcome,
    setUserPermissions,
  } = useAppStore();

  const [checking, setChecking] = useState(true);
  const [brandingLogo, setBrandingLogo] = useState<string>('');
  const [brandingName, setBrandingName] = useState<string>('ORYXOS');
  const [brandingLoaded, setBrandingLoaded] = useState(false);

  // Handle URL query parameters for deep linking
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const viewParam = params.get('view')
    const sessionParam = params.get('session')

    // Handle session=expired — strip it from URL immediately, go to landing
    if (sessionParam === 'expired') {
      // Clean the URL right away — remove all query params
      window.history.replaceState(null, '', window.location.pathname)
      setCurrentView('landing')
      return
    }

    if (viewParam) {
      if (MARKETING_VIEWS.includes(viewParam) || AUTH_VIEWS.includes(viewParam) || viewParam === 'dashboard' || viewParam === 'landing') {
        setCurrentView(viewParam as any)
      }
    }
  }, [setCurrentView])

  // Fetch branding for loading screen (runs first, fast)
  useEffect(() => {
    // Apply accent color from localStorage immediately (before server fetch)
    loadAccentFromStorage()
    fetch('/api/branding')
      .then(res => res.json())
      .then(data => {
        if (data.success && data.settings) {
          if (data.settings.logo_url) setBrandingLogo(data.settings.logo_url)
          if (data.settings.app_name) setBrandingName(data.settings.app_name)
        }
      })
      .catch(() => {})
      .finally(() => setBrandingLoaded(true))
  }, [])

  // Synchronize browser address bar URL with currentView state
  // Track if initial URL has been read (prevent cleaning URL before reading params)
  const urlInitialized = useRef(false)
  
  useEffect(() => {
    if (typeof window !== "undefined") {
      // Skip the first render — let the URL param reader effect run first
      if (!urlInitialized.current) {
        urlInitialized.current = true
        return
      }
      // Build a clean URL — preserve blog slug param for deep linking
      let newUrl = window.location.pathname
      if (currentView !== "landing") {
        newUrl += `?view=${encodeURIComponent(currentView)}`
        // Preserve blog slug if on blog page
        const params = new URLSearchParams(window.location.search)
        const blogSlug = params.get('blog')
        if (currentView === 'blog' && blogSlug) {
          newUrl += `&blog=${encodeURIComponent(blogSlug)}`
        }
      }
      window.history.replaceState(null, "", newUrl)
    }
  }, [currentView]);

  useEffect(() => {
    const root = document.documentElement;
    if (isDark) {
      root.classList.add("dark");
      localStorage.setItem("oryxos-theme", "dark");
    } else {
      root.classList.remove("dark");
      localStorage.setItem("oryxos-theme", "light");
    }
    // Also persist to server so it loads on any device
    if (isAuthenticated) {
      fetch('/api/cpanel/settings', { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ settings: { user_theme: isDark ? 'dark' : 'light' } }) }).catch(() => {})
    }
  }, [isDark, isAuthenticated]);

  // Scroll back to the top of the page on view/page transition
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [currentView]);

  const checkSession = useCallback(async () => {
    const minDelay = new Promise(resolve => setTimeout(resolve, 1200));
    try {
      const res = await fetch("/api/cpanel/auth");
      if (res.ok) {
        const data = await res.json();
        if (data.authenticated && data.user) {
          setUserId(data.user.id || "");
          setUserName(data.user.fullName || data.user.username || "User");
          setUserEmail(data.user.email || "");
          setUserAvatarUrl(data.user.avatarUrl || "");
          setCompanyName(data.tenant?.companyName || "ORYXOS Inc.");
          setTenantSlug(data.tenant?.slug || "");
          setUserRole(data.user.role || "User");
          // Load tenant's selected modules into store for sidebar filtering
          if (data.tenant?.selectedModules && Array.isArray(data.tenant.selectedModules)) {
            setSelectedModules(data.tenant.selectedModules);
          }
          if (data.user?.permissions) {
            setUserPermissions(data.user.permissions);
          }
          setIsAuthenticated(true);
          if (data.user.isNewSession) {
            setShowWelcome(true);
          }

          // Load user preferences from server (theme, language, etc.)
          try {
            const prefRes = await fetch('/api/cpanel/settings')
            if (prefRes.ok) {
              const prefData = await prefRes.json()
              const s = prefData.settings || {}
              // Apply theme preference from server (overrides localStorage)
              if (s.user_theme) {
                const serverDark = s.user_theme === 'dark'
                const { isDark: currentDark } = useAppStore.getState()
                if (serverDark !== currentDark) {
                  useAppStore.getState().toggleTheme()
                }
              }
              // Apply accent color preference from server
              if (s.accentColor) {
                applyAccentColor(s.accentColor)
              }
            }
          } catch {}
        }
      }
    } catch {
      // Ignore
    }
    await minDelay;
    setChecking(false);
  }, [setIsAuthenticated, setUserId, setUserName, setUserEmail, setUserAvatarUrl, setCompanyName, setTenantSlug, setUserRole, setSelectedModules, setShowWelcome, setUserPermissions]);

  useEffect(() => {
    checkSession();
  }, [checkSession]);

  if (checking || !brandingLoaded) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="dashboard-loading-bar" />
        <div className="flex flex-col items-center gap-4">
          {brandingLogo ? (
            <img src={brandingLogo} alt={brandingName} className="h-12 max-w-[180px] object-contain animate-pulse" />
          ) : brandingLoaded ? (
            <div className="w-12 h-12 rounded-xl gradient-brand flex items-center justify-center text-white text-2xl font-bold animate-pulse">
              {brandingName.charAt(0).toUpperCase()}
            </div>
          ) : null}
          <p className="text-sm text-muted-foreground">{brandingLoaded ? `Loading ${brandingName}…` : ''}</p>
        </div>
      </div>
    );
  }

  const isAuthView = AUTH_VIEWS.includes(currentView);
  const isMarketingView = MARKETING_VIEWS.includes(currentView);
  // Cookie notice only on the public site (landing + marketing), not auth/dashboard/cpanel.
  const showCookieConsent = currentView === 'landing' || isMarketingView;

  return (
    <>
    <AnimatePresence mode="wait">
      {isAuthenticated && currentView === "dashboard" ? (
        <motion.div key="dashboard" variants={pageVariants} initial="initial" animate="animate" exit="exit" transition={pageTransition} className="min-h-screen">
          <DashboardShell />
        </motion.div>
      ) : isAuthView ? (
        <motion.div key={currentView} variants={pageVariants} initial="initial" animate="animate" exit="exit" transition={pageTransition} className="min-h-screen">
          <AuthViewRouter view={currentView} />
        </motion.div>
      ) : isMarketingView ? (
        <motion.div key={currentView} variants={pageVariants} initial="initial" animate="animate" exit="exit" transition={pageTransition} className="min-h-screen">
          <MarketingPages />
        </motion.div>
      ) : (
        <motion.div key="landing" variants={pageVariants} initial="initial" animate="animate" exit="exit" transition={pageTransition} className="min-h-screen">
          <LandingPage />
        </motion.div>
      )}
    </AnimatePresence>
    {showCookieConsent && <CookieConsent />}
    </>
  );
}
